/**
 * @file Scoring System Type Definitions
 * @description Contains all TypeScript interfaces related to the Investigation Points (IP) scoring system
 */

import type { ItemWithScene } from './index';

/**
 * Defines a speed streak threshold for multiplier bonuses
 * Used to map streak position to multiplier value
 */
export interface SpeedStreakThreshold {
  minConsecutiveBonuses: number; // Threshold: >= this many consecutive bonuses
  multiplier: number; // Gets this multiplier
  label: string; // e.g., "Speed Demon", "Lightning Reflexes"
}

/**
 * Tracks data about a specific evidence item that was found
 */
export interface EvidenceFind {
  itemId: string;
  timeWhenFound: number; // Timestamp when found (Date.now())
  timeTakenSeconds: number; // Time taken to find this clue (from last find)
  baseTimeEfficiencyBonus: number; // Base time-efficiency bonus before multiplier
}

/**
 * Stores personal best data for cases
 */
export interface PersonalBestData {
  perCase: Record<string, number>; // caseId -> high score
  globalHighScore: number; // Highest score across all cases
}

/**
 * Detailed breakdown of an IP score calculation
 *
 * Scoring components (per spec):
 * 1. Evidence Points: 100 × items found
 * 2. Time-Efficiency Points: Per-find with speed streak multipliers [1.0, 1.6, 2.1, 2.6, 3.1]
 * 3. Subtotal = Evidence Points + Time-Efficiency Points
 * 4. Apply Accuracy Multiplier (0.5× to 1.5×)
 * 5. Apply Pace Multiplier (0.95× to 1.10×)
 * 6. Final IP
 */
export interface IPScoreBreakdown {
  evidencePoints: number; // 100 per evidence item
  timeEfficiencyPoints: number; // Total time-efficiency points with per-find multipliers
  highestSpeedStreak: number; // Highest streak length achieved during run
  subtotal: number; // Evidence + Time-Efficiency (before multipliers)
  accuracyMultiplier: number; // 0.5 to 1.5 based on accuracy (continuous curve)
  paceMultiplier?: number; // 0.95 to 1.10 based on completion time
  scoreBeforePace?: number; // Score after accuracy multiplier, before pace
  totalCompletionTime?: number; // Time from first to last find (seconds)
  finalScore: number; // Final IP score after all multipliers
}

/**
 * Commendation Badge tier enum (based on completion time percentile)
 */
export enum CommendationTier {
  NONE = 0,
  DETECTIVE_GRADE_III = 1,
  DETECTIVE_GRADE_II = 2,
  DETECTIVE_GRADE_I = 3,
  INSPECTOR = 4,
  CHIEF_INSPECTOR = 5,
}

/**
 * Commendation Badge info interface
 */
export interface CommendationBadgeInfo {
  tier: CommendationTier;
  name: string;
  percentile: number;
  description: string;
}

/**
 * Extended level result with IP scoring data
 */
export interface IPLevelResult {
  locationIndex: number;
  locationName: string;
  score: number;
  accuracy: number;
  foundEvidenceCount: number;
  requiredEvidenceCount: number;
  foundItems: ItemWithScene[];
  totalEvidenceCount: number;
  // IP scoring specific fields
  evidenceFinds: EvidenceFind[];
  ipBreakdown: IPScoreBreakdown;
  isNewPersonalBest: boolean;
}

/**
 * Extended game result with IP scoring data
 */
export interface IPGameResult {
  totalScore: number;
  overallAccuracy: number;
  levelResults: IPLevelResult[];
  foundItems: ItemWithScene[];
  totalEvidenceCount: number;
  // IP scoring specific fields
  totalIPBreakdown: IPScoreBreakdown;
  isNewGlobalBest: boolean;

  // Win screen fields
  percentile?: number; // Player's percentile ranking (1-100)
  badgeTier?: number; // Commendation badge tier (0-5)
  badgeName?: string; // Badge name (e.g., "Chief Inspector")
  starRating?: number; // Star rating (0-5)
}
